import { styled, css } from 'styled-components';
import { colors, getFontWeight } from '../../abstracts';
import { createTransition, dontForwardProps } from '../../util';
import { Button } from '../Button';
import { Card } from '../Card';
const getBorderUrl = color => {
  const stroke = color.split('#')[1];
  const url = `"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23${stroke}' stroke-width='2' stroke-dasharray='6%2c 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e"`;
  return css(["background-image:url(", ");"], url);
};
const isEmptyCard = variant => variant === 'empty-default' || variant === 'empty-devices' || variant === 'empty-generic';
export const CardWrapper = styled(Card).withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-1k4ryxb-0"
})(["&&&{line-height:1.3;box-shadow:", ";", " ", " cursor:", ";}&:hover{", " box-shadow:", ";svg path{", " fill:", ";}}&:active{background-image:", ";box-shadow:", ";background-color:", ";.topCircle{background-color:", ";}}&:focus-within{box-shadow:", ";}"], ({
  $variant
}) => isEmptyCard($variant) && 'none', ({
  $variant
}) => isEmptyCard($variant) && getBorderUrl(colors.neutralDeep), createTransition({
  properties: ['background-image', 'box-shadow']
}), ({
  disabled,
  $variant
}) => disabled ? 'not-allowed' : $variant === 'add-remove' ? 'default' : 'pointer', ({
  $variant,
  disabled
}) => isEmptyCard($variant) && !disabled && getBorderUrl(colors.black), ({
  $variant,
  disabled,
  selected
}) => !isEmptyCard($variant) && !disabled && !selected ? `0 0 0 1px ${colors.black} inset` : !isEmptyCard($variant) && !disabled && selected ? `0 0 0 2px ${colors.neutralDeepest} inset` : !isEmptyCard($variant) && disabled && selected && `0 0 0 2px ${colors.neutralDeep} inset`, createTransition({
  properties: ['fill']
}), ({
  disabled,
  $variant
}) => !disabled && $variant !== 'add-remove' && 'unset', ({
  disabled
}) => !disabled && 'none', ({
  $variant,
  disabled,
  selected
}) => isEmptyCard($variant) && disabled ? 'none' : isEmptyCard($variant) ? `0 0 0 1px ${colors.black} inset` : !isEmptyCard($variant) && !selected && !disabled ? `0 0 0 1px ${colors.black} inset` : !isEmptyCard($variant) && selected && disabled ? `0 0 0 2px ${colors.neutralDeep} inset` : !isEmptyCard($variant) && selected ? `0 0 0 2px ${colors.black} inset` : $variant === 'add-remove' && disabled && `0 0 0 1px ${colors.neutralDeep} inset`, ({
  $variant,
  disabled
}) => $variant !== 'add-remove' || disabled ? colors.neutralBrighter : 'inherit', ({
  disabled
}) => !disabled && colors.neutralBright, ({
  $variant,
  disabled
}) => $variant === 'add-remove' && !disabled && `0 0 0 2px ${colors.black} inset`);
export const DeviceCardContainer = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-1"
})(["display:flex;flex-direction:column;align-items:center;height:100%;justify-content:flex-start;position:relative;"]);
export const TopRow = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-2"
})(["display:flex;align-items:center;justify-content:center;"]);
export const BottomRow = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-3"
})([""]);
export const IconContainer = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-4"
})(["display:flex;justify-content:center;align-items:center;margin:0 1.78rem 0;svg path{fill:", ";", "}"], ({
  $variant
}) => isEmptyCard($variant) && colors.neutralBright, createTransition({
  properties: ['fill']
}));
export const Image = styled.img.withConfig({
  componentId: "aui__sc-1k4ryxb-5"
})(["width:100%;max-width:", ";margin:", ";"], ({
  $size
}) => $size === 'sm' ? '84px' : '142px', ({
  $size
}) => $size === 'sm' ? '0 1.78rem 0' : '0');
const getTopCircleBoxShadow = (variant, disabled) => {
  let boxShadow = 'none';
  if (disabled) {
    boxShadow = `0 0 0 2px ${colors.neutralDeep} inset`;
  } else if (isEmptyCard(variant)) {
    boxShadow = `0 0 0 2px ${colors.black} inset`;
  }
  return css(["box-shadow:", ";"], boxShadow);
};
const getCircleStyles = variant => css(["width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:900px;font-weight:", ";overflow:hidden;background-color:", ";"], getFontWeight('heavy'), variant !== 'default' && variant !== 'add-remove' ? 'inherit' : colors.neutralBrighter);
export const TopCircle = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-6"
})(["position:absolute;top:-0.25rem;right:0;", " ", " svg path{fill:", ";}"], ({
  $variant
}) => getCircleStyles($variant), ({
  $variant,
  $disabled
}) => getTopCircleBoxShadow($variant, $disabled), ({
  $variant,
  $disabled
}) => isEmptyCard($variant) && $disabled && colors.neutralDeep);
export const DeviceCardButton = styled(Button).withConfig({
  componentId: "aui__sc-1k4ryxb-7"
})(["margin-top:0.375rem;"]);
export const PrimaryTextWrapper = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-8"
})(["margin-top:0.5rem;word-break:break-all;word-break:break-word;"]);
export const ArrowIconWrapper = styled.span.withConfig({
  componentId: "aui__sc-1k4ryxb-9"
})(["margin-left:2px;svg path{fill:", ";}"], ({
  $disabled
}) => $disabled ? colors.neutralDeep : colors.black);
export const TextWrapper = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-10"
})(["margin-top:0.125rem;color:", ";word-break:break-all;word-break:break-word;"], ({
  $disabled
}) => $disabled ? colors.disabledText : colors.black);
export const TagWrapper = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-11"
})(["margin-top:0.375rem;"]);
export const AddRemoveTextWrapper = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-12"
})(["min-height:", ";text-indent:100%;white-space:nowrap;overflow:hidden;"], ({
  $size
}) => $size === 'sm' ? '1.875rem' : '2.125rem');
export const AddRemoveWrapper = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-13"
})(["background-color:white;width:100%;display:flex;align-items:center;justify-content:space-between;position:absolute;bottom:0;"]);
export const AddRemoveWrapperFixedHeight = styled.div.withConfig({
  componentId: "aui__sc-1k4ryxb-14"
})(["width:100%;display:flex;align-items:center;justify-content:space-between;margin-top:1px;"]);
export const AddRemoveButton = styled(Button).withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-1k4ryxb-15"
})(["&&&{width:32px;height:32px;background-color:", ";border-color:", ";&:hover{background-color:", ";border-color:", ";}&:active{background-color:", ";border-color:", ";}span{display:flex;align-items:center;justify-content:center;}[data-aui='icon']{width:20px;height:20px;}svg path{fill:", ";}}"], ({
  disabled
}) => !disabled && colors.black, ({
  disabled
}) => disabled ? colors.neutralDeep : colors.black, ({
  disabled
}) => !disabled && colors.neutralDeepest, ({
  disabled
}) => disabled ? colors.neutralDeep : colors.neutralDeepest, ({
  disabled
}) => !disabled && colors.black, ({
  disabled
}) => disabled ? colors.neutralDeep : colors.black, ({
  disabled
}) => disabled ? colors.neutralDeep : colors.white);
export const AddRemoveCounter = styled.span.withConfig({
  componentId: "aui__sc-1k4ryxb-16"
})(["", ""], ({
  $variant
}) => getCircleStyles($variant));